import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { ProjectsService } from '../../../../../../../core/projects.service';
import { CreateOrEditSiteComponent } from '../create-or-edit-site/create-or-edit-site.component';
import { FetchSitesListRequest, DeleteSiteRequest } from '../../../../../../../core/projects.actions';
import { sitesListInfo } from '../../../../../../../core/projects.selectors';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../../shared/components';

@Component({
    selector: 'app-sites-list',
    templateUrl: './sites-list.component.html',
    styleUrls: ['./sites-list.component.scss'],
    standalone: false
})
export class SitesListComponent implements OnInit, OnDestroy {
  projectId: string;

  displayedColumns: string[] = ['name', 'description', 'progress', 'site_value', 'actions'];
  dataSource: any[] = [];
  filteredDataSource: any[] = [];
  originalDataSource: any[] = [];
  selectedRow: any = null;
  totalRecords = 0;
  pagination = {
    pageIndex: 0,
    pageSize: 10,
  };
  currentPage = 1;
  itemsPerPage = 10;

  private readonly onDestroy = new Subject<void>();

  constructor(
    private projectsService: ProjectsService,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this.onDestroy)).subscribe(qp => {
      if (qp.subTab === 'sites') {
        if (this.route.snapshot.paramMap.has('projectId')) {
          this.projectId = this.route.snapshot.paramMap.get('projectId');
          this.fetchSitesList();
        } else {
          const url = this.router.url;
          const path = url.split('?')[0];
          const segments = path.split('/');
          this.projectId = segments[segments.length - 1];
          if (this.projectId) {
            this.fetchSitesList();
          }
        }
      }
    });

    this.store
      .select(sitesListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          data = JSON.parse(JSON.stringify(data));
          this.currentPage = 1;

          if (data && data?.list?.length > 0) {
            this.originalDataSource = data?.list;
            this.filteredDataSource = [...this.originalDataSource];
            this.totalRecords = data.totalRecords || this.originalDataSource.length;
            this.paginateData(true);
          } else {
            this.originalDataSource = [];
            this.filteredDataSource = [];
            this.dataSource = [];
            this.totalRecords = 0;
          }
        }
      });
  }

  fetchSitesList(): void {
    const payload = {
      projectId: this.projectId,
      qp: {
        start: 0,
        total: 1000,
      },
    };

    if (this.projectId) {
      this.store.dispatch(FetchSitesListRequest({ payload }));
    }
  }

  rowClicked(row: any): void {
    this.selectedRow = row;

    this.projectsService.openRightPanel.next({
      component: CreateOrEditSiteComponent,
      data: {
        ...row,
        projectId: this.projectId,
      },
    });
  }

  create(): void {
    this.projectsService.openRightPanel.next({
      component: CreateOrEditSiteComponent,
      data: {
        projectId: this.projectId,
      },
    });
  }

  onPageChanged(page: number): void {
    this.currentPage = page;
    this.pagination.pageIndex = page - 1;
    this.paginateData();
  }

  paginateData(reset = false): void {
    if (reset) {
      this.currentPage = 1;
    }
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = Math.min(startIndex + this.itemsPerPage, this.filteredDataSource.length);

    this.dataSource = this.filteredDataSource.slice(startIndex, endIndex);
  }

  deleteSite(row: any, event: Event): void {
    event.stopPropagation();
    const siteName = row?.name || 'this site';
    const message = `Deleting <b>${siteName}</b> cannot be undone.<br />Proceed to delete?`;
    const dialogData = new ConfirmDialogModel('Delete?', message);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && this.projectId) {
        const payload = {
          projectId: this.projectId,
          siteId: row?.id || row?.uuid,
        };
        this.store.dispatch(DeleteSiteRequest({ payload }));
      }
    });
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
